// Centralized, safe French mapping for Firebase Auth errors. Normalizes both
// SDK-thrown errors (error.code, e.g. "auth/operation-not-allowed", from
// Google popup/redirect and the magic-link SDK calls) and the REST
// adapter's thrown errors (error.message carrying Identity Toolkit's own
// UPPER_SNAKE_CASE strings, e.g. "OPERATION_NOT_ALLOWED", from password
// login/signup/reset — see config/supabase.js's firebaseFetch) into one
// lookup. Never falls back to the raw error text: unrecognized errors get
// only the generic message, so a raw "Firebase: Error (auth/...)" string
// can never reach the UI through this function.
const GENERIC_FALLBACK = 'Une erreur est survenue. Réessayez.';

// P-AUTH-EMAIL-UX-1: rate limiting on the EMAIL-VERIFICATION flow (pending
// screen's resend / re-check actions) gets calmer, flow-specific copy — it
// must reassure that a previously received link stays usable and point at
// Spam, never "Trop de tentatives" (which reads like the account itself is
// in trouble). Every other flow (login, password reset, …) keeps the
// shorter generic text from RULES below.
const VERIFICATION_RATE_LIMIT =
  'Veuillez patienter quelques minutes avant de demander un nouvel e-mail. ' +
  'Un lien déjà reçu peut toujours être utilisé : pensez à vérifier vos courriers indésirables.';
// Identity Toolkit (REST) sends TOO_MANY_ATTEMPTS_TRY_LATER; the SDK uses
// auth/too-many-requests — both spellings normalize into these tokens.
const RATE_LIMIT_TOKENS = ['TOO_MANY_REQUESTS', 'TOO_MANY_ATTEMPTS'];

// Wrong-password/no-such-account/bad-credential are deliberately bucketed
// into one identical message — distinguishing them would let an attacker
// enumerate which e-mail addresses have an NSARA account.
const RULES = [
  { tokens: ['OPERATION_NOT_ALLOWED'], text: "Cette méthode de connexion n'est pas disponible pour le moment." },
  { tokens: ['INVALID_EMAIL'], text: "Cette adresse e-mail n'est pas valide." },
  // EXPIRED/INVALID_OOB_CODE: the REST (Identity Toolkit) names; the SDK
  // uses *_ACTION_CODE for the same conditions — both come from verification
  // and password-reset links (accounts:sendOobCode / applyActionCode).
  { tokens: ['EXPIRED_OOB_CODE', 'EXPIRED_ACTION_CODE'], text: 'Ce lien a expiré. Renvoyez un nouveau lien.' },
  { tokens: ['INVALID_OOB_CODE', 'INVALID_ACTION_CODE'], text: 'Ce lien est invalide. Renvoyez un nouveau lien.' },
  { tokens: ['INVALID_CREDENTIAL', 'INVALID_LOGIN_CREDENTIALS', 'WRONG_PASSWORD', 'USER_NOT_FOUND', 'EMAIL_NOT_FOUND', 'INVALID_PASSWORD'], text: 'E-mail ou mot de passe incorrect.' },
  { tokens: RATE_LIMIT_TOKENS, text: 'Trop de tentatives. Réessayez dans quelques instants.' },
  { tokens: ['NETWORK_REQUEST_FAILED', 'FAILED_TO_FETCH', 'NETWORK_ERROR'], text: 'Impossible de contacter le service. Vérifiez votre connexion et réessayez.' },
  { tokens: ['USER_DISABLED'], text: "Ce compte n'est pas disponible." },
];

// `context` is optional and additive: callers that pass 'email-verification'
// get the flow-specific rate-limit copy above; every existing caller
// (no context) keeps exactly the mapping it had before.
export const authErrorMessage = (error, context) => {
  const normalized = `${error?.code || ''} ${error?.message || ''}`.toUpperCase().replace(/-/g, '_');
  if (context === 'email-verification' && RATE_LIMIT_TOKENS.some((token) => normalized.includes(token))) {
    return VERIFICATION_RATE_LIMIT;
  }
  const rule = RULES.find(({ tokens }) => tokens.some((token) => normalized.includes(token)));
  return rule ? rule.text : GENERIC_FALLBACK;
};
